import * as React from "react"

/**
 * Registry of live DOM elements keyed by scene object id.
 *
 * `masterTimeline.slice` and `tween.helpers` need to reach the element of an object from
 * outside React (to build GSAP tweens and to reset inline styles), so the elements are
 * published into a module-level map instead of being drilled through props.
 *
 * Implemented with CALLBACK refs on purpose. The previous version called
 * `React.createRef()` inside `setRef()` — i.e. during render — and overwrote the map entry:
 *
 * - between render and commit `map.get(id).current` was `null`, so a timeline rebuild in
 *   that window silently produced no-op tweens ("Target for objectId ... not found")
 * - a render React threw away left a ref in the map that was never attached to anything
 * - nothing ever removed an entry, so every element the editor had ever mounted was kept
 *   alive by the map — detached DOM nodes that no snapshot could collect
 *
 * A callback is created once per key and reused, so React only invokes it when the element
 * actually attaches or detaches, not on every render.
 */
type Entry = {
    current: Node | null
    /** every element currently claiming this key — see the note in `setRef` */
    nodes: Set<Node>
}

const refs = new Map<string, Entry>()
const callbacks = new Map<string, React.RefCallback<never>>()

/**
 * Decide, after the commit has settled, which of the elements registered under `key` are
 * still in the document.
 */
const prune = (key: string): void => {
    const entry = refs.get(key)
    if (!entry) return

    entry.nodes.forEach((node) => {
        if (!node.isConnected) entry.nodes.delete(node)
    })

    if (entry.nodes.size === 0) {
        refs.delete(key)
        callbacks.delete(key)
        return
    }

    if (!entry.current || !entry.current.isConnected) {
        entry.current = Array.from(entry.nodes)[entry.nodes.size - 1]
    }
}

function getRef<T>(key: string): React.RefObject<T> | undefined {
    return refs.get(key) as React.RefObject<T> | undefined
}

function setRef<T>(key: string): React.RefCallback<T> {
    const cached = callbacks.get(key)
    if (cached) return cached as React.RefCallback<T>

    const callback = (node: T | null) => {
        if (node) {
            const entry = refs.get(key)
            if (entry) {
                entry.nodes.add(node as unknown as Node)
                entry.current = node as unknown as Node
            } else {
                refs.set(key, {
                    current: node as unknown as Node,
                    nodes: new Set([node as unknown as Node]),
                })
            }
            return
        }

        // React does not say WHICH element detached, and one object id can legitimately be
        // mounted twice: while a multiselection is active `ObjectsWrapper` renders a copy of
        // each selected object, and the original stays mounted in its parent, merely hidden.
        // Both get this same cached callback, so simply deleting the entry here would throw
        // away the ref of an element that is still on screen.
        //
        // Deferring to a microtask lets the commit finish first; the DOM itself then says
        // which nodes survived.
        queueMicrotask(() => prune(key))
    }

    callbacks.set(key, callback as React.RefCallback<never>)
    return callback
}

export default function useDynamicRefs<T>(): [
    (key: string) => React.RefObject<T> | undefined,
    (key: string) => React.RefCallback<T>
] {
    return [getRef, setRef]
}
